import { Injectable, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { isTestAutomationAuth } from '@helios/auth'
import { RootState, User, UserPolicy } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { CwanEnvironment } from 'environments/environment'
import { Subject } from 'rxjs'
import { FullStoryService } from './fullstory.service'

const HELIOS_FEEDBACK = 31311

/**
 * Telemetry service wraps FullStory to initialize and send custom metrics/events.
 * NAMESPACE PREFIX: heliosXXX - all metrics and events must be prefixed with helios - e.g. heliosAccountId - to avoid conflicts
 *   exceptions: FullStory already has userId/clientId without prefix from prior apps, so we stick to that pattern.
 */
@Injectable({
  providedIn: 'root',
})
export class TelemetryService implements OnDestroy {
  private fullStory?: FullStoryService
  private user: User | undefined
  private readonly _destroyed$ = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    fullStory: FullStoryService,
    private readonly router: Router,
  ) {
    this.store = store
    this.fullStory = fullStory
  }

  init(user: User, userPolicy: UserPolicy, environment: CwanEnvironment) {
    this.user = user

    //only capture telemetry in prd and stg
    if (environment != 'prod' && environment != 'staging') {
      this.fullStory = undefined
      return
    }

    //Do not record automated testing - it's just a lot of noise.
    //Also, for some reason NR throws an exception:
    // ```
    // Uncaught TypeError: Cannot read properties of undefined (reading 'length')
    // at o (nr-spa-1215.min.js:2:8060)
    // ...
    // ```
    if (isTestAutomationAuth()) {
      this.fullStory = undefined
      return
    }

    this.detectAdBlocker()

    this.fullStory?.init(userPolicy)
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  sendFeedback() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fieldValues = (window as any).ATL_JQ_PAGE_PROPS.fieldValues
    fieldValues.fullname = this.user?.name
    fieldValues.email = this.user?.email
    fieldValues.components = HELIOS_FEEDBACK
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).JIRA_CONNECTION()
  }

  /**
   * please prefix all events with 'helios'
   */
  emitCustomEvent(
    eventName: string,
    attributes: {
      [key: string]: string | number | undefined
    },
  ) {
    this.fullStory?.emitCustomEvent(eventName, attributes)
  }

  setCustomAttributes(attributes: {
    [key: string]: string | number | undefined
  }) {
    this.fullStory?.setCustomAttributes(attributes)
  }

  detectAdBlocker() {
    this.fullStory?.isEnabled.subscribe(enabled => {
      if (enabled && this.hasAdBlockerPath()) {
        this.router.navigate([''])
      } else if (!enabled && !this.hasAdBlockerPath()) {
        this.router.navigate(['/ad-blocker'])
      }
    })
  }

  hasAdBlockerPath() {
    return window.location.pathname.includes('/ad-blocker')
  }
}
